import { MIN_SPACE_DIM_FEET, STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  FootprintPolygonsType,
  GridSpaceDataType,
  PositionAndDimensionsType,
  PositionType,
  SpaceType
} from 'data/types';
import {
  isPointInPolygon,
  isPointInsideOrOnPolygon,
  isRectInPolygon,
  polygonBoundingBox
} from './footprint';
import { edgeForOutwardNormal } from './edge-exposure';
import {
  findContainingPolygon,
  isPointInAnyPolygon
} from './footprint-polygons';
import { isValidFootprintShape } from './footprint-union';
import { snapToBoundary } from './space-polygon-helpers';

function validFootprints(
  footprints: FootprintPolygonsType | null | undefined
): PositionType[][] {
  return (footprints ?? []).filter(isValidFootprintShape);
}

const AXIS_ALIGN_EPS = 1e-3;
const NORMAL_EPS = 1e-6;

export type RelativeVertex = PositionType;

export type AutoExtendedEdge = {
  prevA: PositionType;
  prevB: PositionType;
  currA: PositionType;
  currB: PositionType;
};

export function rectToVertices(
  width: number,
  length: number
): RelativeVertex[] {
  return [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: length },
    { x: 0, y: length }
  ];
}

export function isRectangleBboxVertices(
  vertices: RelativeVertex[] | undefined | null,
  width: number,
  height: number
): boolean {
  if (!vertices) return true;
  if (vertices.length !== 4) return false;
  const tol = 0.5;
  const corners = new Set([
    '0,0',
    `${width},0`,
    `${width},${height}`,
    `0,${height}`
  ]);
  return vertices.every((v) => {
    const vx = snapToBoundary(v.x, width, tol);
    const vy = snapToBoundary(v.y, height, tol);
    if (Number.isNaN(vx) || Number.isNaN(vy)) return false;
    return corners.has(`${vx},${vy}`);
  });
}

export function isPolygonGridSpace(
  vertices: RelativeVertex[] | undefined | null,
  width: number,
  height: number
): boolean {
  return (
    !!vertices &&
    vertices.length >= 3 &&
    !isRectangleBboxVertices(vertices, width, height)
  );
}

export function isRectangularVertices(
  vertices: RelativeVertex[],
  width: number,
  height: number,
  tolerance: number = 0.5
): boolean {
  if (vertices.length !== 4) return false;
  const corners = new Set([
    '0,0',
    `${width},0`,
    `${width},${height}`,
    `0,${height}`
  ]);
  return vertices.every((v) => {
    const vx = snapToBoundary(v.x, width, tolerance);
    const vy = snapToBoundary(v.y, height, tolerance);
    if (Number.isNaN(vx) || Number.isNaN(vy)) return false;
    return corners.has(`${vx},${vy}`);
  });
}

export function polygonArea(vertices: RelativeVertex[]): number {
  if (vertices.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < vertices.length; i += 1) {
    const j = (i + 1) % vertices.length;
    area += vertices[i].x * vertices[j].y - vertices[j].x * vertices[i].y;
  }
  return Math.abs(area) / 2;
}

export type SpaceShapeId =
  | 'rectangle'
  | 'rectangle-landscape'
  | 'rectangle-portrait'
  | 'triangle'
  | 'triangle-up'
  | 'triangle-right'
  | 'triangle-down'
  | 'triangle-left'
  | 'T'
  | 'T-up'
  | 'T-right'
  | 'T-down'
  | 'T-left'
  | 'L'
  | 'L-tl'
  | 'L-tr'
  | 'L-br'
  | 'L-bl'
  | 'U'
  | 'U-up'
  | 'U-down'
  | 'U-left'
  | 'U-right'
  | '+';

const RECTANGLE_SHAPE_IDS: ReadonlySet<SpaceShapeId> = new Set<SpaceShapeId>([
  'rectangle',
  'rectangle-landscape',
  'rectangle-portrait'
]);

export function isRectangleShape(shape: SpaceShapeId): boolean {
  return RECTANGLE_SHAPE_IDS.has(shape);
}

export type SpaceShapeLayout = {
  position: { x: number; y: number };
  vertices: RelativeVertex[];
};

const TRIANGLE_UP_VERTICES: RelativeVertex[] = [
  { x: 6, y: 0 },
  { x: 12, y: 12 },
  { x: 0, y: 12 }
];

const TRIANGLE_RIGHT_VERTICES: RelativeVertex[] = [
  { x: 12, y: 6 },
  { x: 0, y: 12 },
  { x: 0, y: 0 }
];

const TRIANGLE_DOWN_VERTICES: RelativeVertex[] = [
  { x: 6, y: 12 },
  { x: 0, y: 0 },
  { x: 12, y: 0 }
];

const TRIANGLE_LEFT_VERTICES: RelativeVertex[] = [
  { x: 0, y: 6 },
  { x: 12, y: 0 },
  { x: 12, y: 12 }
];

const T_DOWN_VERTICES: RelativeVertex[] = [
  { x: 0, y: 0 },
  { x: 16, y: 0 },
  { x: 16, y: 6 },
  { x: 12, y: 6 },
  { x: 12, y: 16 },
  { x: 4, y: 16 },
  { x: 4, y: 6 },
  { x: 0, y: 6 }
];

const T_RIGHT_VERTICES: RelativeVertex[] = [
  { x: 0, y: 16 },
  { x: 0, y: 0 },
  { x: 6, y: 0 },
  { x: 6, y: 4 },
  { x: 16, y: 4 },
  { x: 16, y: 12 },
  { x: 6, y: 12 },
  { x: 6, y: 16 }
];

const T_UP_VERTICES: RelativeVertex[] = [
  { x: 16, y: 16 },
  { x: 0, y: 16 },
  { x: 0, y: 10 },
  { x: 4, y: 10 },
  { x: 4, y: 0 },
  { x: 12, y: 0 },
  { x: 12, y: 10 },
  { x: 16, y: 10 }
];

const T_LEFT_VERTICES: RelativeVertex[] = [
  { x: 16, y: 0 },
  { x: 16, y: 16 },
  { x: 10, y: 16 },
  { x: 10, y: 12 },
  { x: 0, y: 12 },
  { x: 0, y: 4 },
  { x: 10, y: 4 },
  { x: 10, y: 0 }
];

const L_TL_VERTICES: RelativeVertex[] = [
  { x: 9, y: 0 },
  { x: 16, y: 0 },
  { x: 16, y: 16 },
  { x: 0, y: 16 },
  { x: 0, y: 9 },
  { x: 9, y: 9 }
];

const L_BL_VERTICES: RelativeVertex[] = [
  { x: 0, y: 7 },
  { x: 0, y: 0 },
  { x: 16, y: 0 },
  { x: 16, y: 16 },
  { x: 9, y: 16 },
  { x: 9, y: 7 }
];

const L_BR_VERTICES: RelativeVertex[] = [
  { x: 7, y: 16 },
  { x: 0, y: 16 },
  { x: 0, y: 0 },
  { x: 16, y: 0 },
  { x: 16, y: 7 },
  { x: 7, y: 7 }
];

const L_TR_VERTICES: RelativeVertex[] = [
  { x: 16, y: 9 },
  { x: 16, y: 16 },
  { x: 0, y: 16 },
  { x: 0, y: 0 },
  { x: 7, y: 0 },
  { x: 7, y: 9 }
];

const PLUS_SHAPE_VERTICES: RelativeVertex[] = [
  { x: 6, y: 0 },
  { x: 14, y: 0 },
  { x: 14, y: 6 },
  { x: 20, y: 6 },
  { x: 20, y: 14 },
  { x: 14, y: 14 },
  { x: 14, y: 20 },
  { x: 6, y: 20 },
  { x: 6, y: 14 },
  { x: 0, y: 14 },
  { x: 0, y: 6 },
  { x: 6, y: 6 }
];

const U_UP_VERTICES: RelativeVertex[] = [
  { x: 0, y: 0 },
  { x: 4, y: 0 },
  { x: 4, y: 8 },
  { x: 12, y: 8 },
  { x: 12, y: 0 },
  { x: 16, y: 0 },
  { x: 16, y: 12 },
  { x: 0, y: 12 }
];

const U_DOWN_VERTICES: RelativeVertex[] = [
  { x: 0, y: 0 },
  { x: 16, y: 0 },
  { x: 16, y: 12 },
  { x: 12, y: 12 },
  { x: 12, y: 4 },
  { x: 4, y: 4 },
  { x: 4, y: 12 },
  { x: 0, y: 12 }
];

const U_LEFT_VERTICES: RelativeVertex[] = [
  { x: 0, y: 0 },
  { x: 12, y: 0 },
  { x: 12, y: 16 },
  { x: 0, y: 16 },
  { x: 0, y: 12 },
  { x: 8, y: 12 },
  { x: 8, y: 4 },
  { x: 0, y: 4 }
];

const U_RIGHT_VERTICES: RelativeVertex[] = [
  { x: 0, y: 0 },
  { x: 12, y: 0 },
  { x: 12, y: 4 },
  { x: 4, y: 4 },
  { x: 4, y: 12 },
  { x: 12, y: 12 },
  { x: 12, y: 16 },
  { x: 0, y: 16 }
];

const T_OFFSET = { x: -2, y: -2 };
const L_OFFSET = { x: -2, y: -2 };
const U_HORIZONTAL_OFFSET = { x: -2, y: 0 };
const U_VERTICAL_OFFSET = { x: 0, y: -2 };
const PLUS_OFFSET = { x: -4, y: -4 };

export function getSpaceShapeLayout(
  shape: SpaceShapeId,
  basePosition: { x: number; y: number }
): SpaceShapeLayout {
  switch (shape) {
    case 'triangle':
    case 'triangle-up':
      return {
        position: { x: basePosition.x, y: basePosition.y },
        vertices: TRIANGLE_UP_VERTICES
      };
    case 'triangle-right':
      return {
        position: { x: basePosition.x, y: basePosition.y },
        vertices: TRIANGLE_RIGHT_VERTICES
      };
    case 'triangle-down':
      return {
        position: { x: basePosition.x, y: basePosition.y },
        vertices: TRIANGLE_DOWN_VERTICES
      };
    case 'triangle-left':
      return {
        position: { x: basePosition.x, y: basePosition.y },
        vertices: TRIANGLE_LEFT_VERTICES
      };
    case 'T':
    case 'T-down':
      return {
        position: {
          x: basePosition.x + T_OFFSET.x,
          y: basePosition.y + T_OFFSET.y
        },
        vertices: T_DOWN_VERTICES
      };
    case 'T-right':
      return {
        position: {
          x: basePosition.x + T_OFFSET.x,
          y: basePosition.y + T_OFFSET.y
        },
        vertices: T_RIGHT_VERTICES
      };
    case 'T-up':
      return {
        position: {
          x: basePosition.x + T_OFFSET.x,
          y: basePosition.y + T_OFFSET.y
        },
        vertices: T_UP_VERTICES
      };
    case 'T-left':
      return {
        position: {
          x: basePosition.x + T_OFFSET.x,
          y: basePosition.y + T_OFFSET.y
        },
        vertices: T_LEFT_VERTICES
      };
    case 'L':
    case 'L-tl':
      return {
        position: {
          x: basePosition.x + L_OFFSET.x,
          y: basePosition.y + L_OFFSET.y
        },
        vertices: L_TL_VERTICES
      };
    case 'L-br':
      return {
        position: {
          x: basePosition.x + L_OFFSET.x,
          y: basePosition.y + L_OFFSET.y
        },
        vertices: L_BR_VERTICES
      };
    case 'L-bl':
      return {
        position: {
          x: basePosition.x + L_OFFSET.x,
          y: basePosition.y + L_OFFSET.y
        },
        vertices: L_BL_VERTICES
      };
    case 'L-tr':
      return {
        position: {
          x: basePosition.x + L_OFFSET.x,
          y: basePosition.y + L_OFFSET.y
        },
        vertices: L_TR_VERTICES
      };
    case 'U':
    case 'U-up':
      return {
        position: {
          x: basePosition.x + U_HORIZONTAL_OFFSET.x,
          y: basePosition.y + U_HORIZONTAL_OFFSET.y
        },
        vertices: U_UP_VERTICES
      };
    case 'U-down':
      return {
        position: {
          x: basePosition.x + U_HORIZONTAL_OFFSET.x,
          y: basePosition.y + U_HORIZONTAL_OFFSET.y
        },
        vertices: U_DOWN_VERTICES
      };
    case 'U-left':
      return {
        position: {
          x: basePosition.x + U_VERTICAL_OFFSET.x,
          y: basePosition.y + U_VERTICAL_OFFSET.y
        },
        vertices: U_LEFT_VERTICES
      };
    case 'U-right':
      return {
        position: {
          x: basePosition.x + U_VERTICAL_OFFSET.x,
          y: basePosition.y + U_VERTICAL_OFFSET.y
        },
        vertices: U_RIGHT_VERTICES
      };
    case '+':
      return {
        position: {
          x: basePosition.x + PLUS_OFFSET.x,
          y: basePosition.y + PLUS_OFFSET.y
        },
        vertices: PLUS_SHAPE_VERTICES
      };
    case 'rectangle-landscape':
      return {
        position: { x: basePosition.x, y: basePosition.y },
        vertices: rectToVertices(16, 10)
      };
    case 'rectangle-portrait':
      return {
        position: { x: basePosition.x, y: basePosition.y },
        vertices: rectToVertices(10, 16)
      };
    case 'rectangle':
    default:
      return {
        position: { x: basePosition.x, y: basePosition.y },
        vertices: rectToVertices(12, 12)
      };
  }
}

export function boundingBoxOfVertices(vertices: RelativeVertex[]): {
  width: number;
  height: number;
  minX: number;
  minY: number;
} {
  if (vertices.length === 0) {
    return { width: 0, height: 0, minX: 0, minY: 0 };
  }
  const bb = polygonBoundingBox(vertices);
  return {
    minX: bb.minX,
    minY: bb.minY,
    width: bb.maxX - bb.minX,
    height: bb.maxY - bb.minY
  };
}

export function normalizeVerticesToOrigin(vertices: RelativeVertex[]): {
  vertices: RelativeVertex[];
  dx: number;
  dy: number;
} {
  const bb = boundingBoxOfVertices(vertices);
  if (bb.minX === 0 && bb.minY === 0) {
    return { vertices, dx: 0, dy: 0 };
  }
  return {
    vertices: vertices.map((v) => ({ x: v.x - bb.minX, y: v.y - bb.minY })),
    dx: bb.minX,
    dy: bb.minY
  };
}

export function assignSpaceRectShape<
  T extends { width?: number; length?: number; vertices?: RelativeVertex[] }
>(space: T, width: number, length: number): void {
  /* eslint-disable no-param-reassign */
  space.width = width;
  space.length = length;
  space.vertices = rectToVertices(width, length);
  /* eslint-enable no-param-reassign */
}

export function assignSpacePolygonShape<
  T extends { width?: number; length?: number; vertices?: RelativeVertex[] }
>(space: T, vertices: RelativeVertex[]): void {
  const normalized = normalizeVerticesToOrigin(vertices);
  const bb = boundingBoxOfVertices(normalized.vertices);
  const isRect = isRectangleBboxVertices(
    normalized.vertices,
    bb.width,
    bb.height
  );
  /* eslint-disable no-param-reassign */
  space.vertices = normalized.vertices;
  space.width = isRect ? bb.width : undefined;
  space.length = isRect ? bb.height : undefined;
  /* eslint-enable no-param-reassign */
}

export function getSpaceVertices(
  space: Pick<SpaceType, 'vertices' | 'width' | 'length'>
): RelativeVertex[] {
  if (space.vertices && space.vertices.length >= 3) {
    return space.vertices;
  }
  const width = space.width ?? 0;
  const length = space.length ?? 0;
  return rectToVertices(width, length);
}

export function getSpaceOverlapRect(
  space: Pick<SpaceType, 'position' | 'vertices' | 'width' | 'length'>
): PositionAndDimensionsType | null {
  if (!space.position) return null;
  if (space.width && space.length) {
    return {
      x: space.position.x,
      y: space.position.y,
      width: space.width,
      height: space.length
    };
  }
  if (space.vertices && space.vertices.length >= 3) {
    const bb = boundingBoxOfVertices(space.vertices);
    return {
      x: space.position.x + bb.minX,
      y: space.position.y + bb.minY,
      width: bb.width,
      height: bb.height
    };
  }
  return null;
}

export function verticesToAbsolute(
  vertices: RelativeVertex[],
  origin: { x: number; y: number }
): PositionType[] {
  return vertices.map((v) => ({ x: v.x + origin.x, y: v.y + origin.y }));
}

export function verticesToRelative(
  vertices: PositionType[],
  origin: { x: number; y: number }
): RelativeVertex[] {
  return vertices.map((v) => ({ x: v.x - origin.x, y: v.y - origin.y }));
}

export function scaleVertices(
  vertices: RelativeVertex[],
  scale: number
): RelativeVertex[] {
  return vertices.map((v) => ({ x: v.x * scale, y: v.y * scale }));
}

export function verticesToSvgPath(vertices: RelativeVertex[]): string {
  if (vertices.length < 3) return '';
  return vertices
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ')
    .concat(' Z');
}

export function verticesToCssPolygon(vertices: RelativeVertex[]): string {
  if (vertices.length < 3) return '';
  const points = vertices.map((p) => `${p.x}px ${p.y}px`).join(', ');
  return `polygon(${points})`;
}

function segmentsIntersect(
  a1: PositionType,
  a2: PositionType,
  b1: PositionType,
  b2: PositionType
): boolean {
  const d = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
  if (d === 0) return false;
  const t = ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / d;
  const u = ((b1.x - a1.x) * (a2.y - a1.y) - (b1.y - a1.y) * (a2.x - a1.x)) / d;
  return t > 0 && t < 1 && u > 0 && u < 1;
}

export function polygonsOverlap(a: PositionType[], b: PositionType[]): boolean {
  if (a.length < 3 || b.length < 3) return false;

  for (const v of a) {
    if (isPointInPolygon(v, b)) return true;
  }
  for (const v of b) {
    if (isPointInPolygon(v, a)) return true;
  }

  for (let i = 0; i < a.length; i += 1) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j += 1) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }

  return false;
}

export function rectToAbsolutePolygon(
  rect: PositionAndDimensionsType
): PositionType[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height }
  ];
}

type Edge = 'top' | 'right' | 'bottom' | 'left';

function rayHitsSegment(
  origin: PositionType,
  direction: { x: number; y: number },
  seg1: PositionType,
  seg2: PositionType
): number | null {
  const sx = seg2.x - seg1.x;
  const sy = seg2.y - seg1.y;
  const denom = direction.x * -sy - direction.y * -sx;
  if (denom === 0) return null;
  const t = ((seg1.x - origin.x) * -sy - (seg1.y - origin.y) * -sx) / denom;
  const u =
    ((seg1.x - origin.x) * -direction.y - (seg1.y - origin.y) * -direction.x) /
    denom;
  if (t < 0 || u < 0 || u > 1) return null;
  return t;
}

function getEdgeRayOrigins(
  rect: PositionAndDimensionsType,
  edge: Edge
): { origins: PositionType[]; direction: { x: number; y: number } } {
  const edgeLength =
    edge === 'top' || edge === 'bottom' ? rect.width : rect.height;
  const sampleCount = Math.max(3, Math.ceil(edgeLength) + 1);
  const origins: PositionType[] = [];

  if (edge === 'top') {
    for (let i = 0; i < sampleCount; i += 1) {
      const t = i / (sampleCount - 1);
      origins.push({ x: rect.x + rect.width * t, y: rect.y });
    }
    return { origins, direction: { x: 0, y: -1 } };
  }
  if (edge === 'bottom') {
    for (let i = 0; i < sampleCount; i += 1) {
      const t = i / (sampleCount - 1);
      origins.push({ x: rect.x + rect.width * t, y: rect.y + rect.height });
    }
    return { origins, direction: { x: 0, y: 1 } };
  }
  if (edge === 'left') {
    for (let i = 0; i < sampleCount; i += 1) {
      const t = i / (sampleCount - 1);
      origins.push({ x: rect.x, y: rect.y + rect.height * t });
    }
    return { origins, direction: { x: -1, y: 0 } };
  }
  for (let i = 0; i < sampleCount; i += 1) {
    const t = i / (sampleCount - 1);
    origins.push({
      x: rect.x + rect.width,
      y: rect.y + rect.height * t
    });
  }
  return { origins, direction: { x: 1, y: 0 } };
}

function edgeRayHits(
  rect: PositionAndDimensionsType,
  edge: Edge,
  footprint: PositionType[]
): number[] {
  const { origins, direction } = getEdgeRayOrigins(rect, edge);
  const hits: number[] = [];
  for (const origin of origins) {
    let firstHit = Infinity;
    for (let i = 0; i < footprint.length; i += 1) {
      const p1 = footprint[i];
      const p2 = footprint[(i + 1) % footprint.length];
      const t = rayHitsSegment(origin, direction, p1, p2);
      if (t !== null && t > 0 && t < firstHit) {
        firstHit = t;
      }
    }
    if (Number.isFinite(firstHit)) {
      hits.push(firstHit);
    }
  }
  return hits;
}

function minRayDistance(hits: number[]): number {
  let result = Infinity;
  for (const h of hits) {
    if (h > 0 && h < result) result = h;
  }
  return result;
}

function maxRayDistance(hits: number[]): number {
  let result = 0;
  for (const h of hits) {
    if (h > 0 && h > result) result = h;
  }
  return result;
}

function getExtensionSweepPolygon(
  rect: PositionAndDimensionsType,
  edge: Edge,
  distance: number,
  perpendicularInset: number = 0
): PositionType[] {
  if (edge === 'top') {
    const left = rect.x + perpendicularInset;
    const right = rect.x + rect.width - perpendicularInset;
    return [
      { x: left, y: rect.y },
      { x: right, y: rect.y },
      { x: right, y: rect.y - distance },
      { x: left, y: rect.y - distance }
    ];
  }
  if (edge === 'bottom') {
    const left = rect.x + perpendicularInset;
    const right = rect.x + rect.width - perpendicularInset;
    return [
      { x: left, y: rect.y + rect.height },
      { x: right, y: rect.y + rect.height },
      { x: right, y: rect.y + rect.height + distance },
      { x: left, y: rect.y + rect.height + distance }
    ];
  }
  if (edge === 'left') {
    const top = rect.y + perpendicularInset;
    const bottom = rect.y + rect.height - perpendicularInset;
    return [
      { x: rect.x, y: top },
      { x: rect.x - distance, y: top },
      { x: rect.x - distance, y: bottom },
      { x: rect.x, y: bottom }
    ];
  }
  const top = rect.y + perpendicularInset;
  const bottom = rect.y + rect.height - perpendicularInset;
  return [
    { x: rect.x + rect.width, y: top },
    { x: rect.x + rect.width + distance, y: top },
    { x: rect.x + rect.width + distance, y: bottom },
    { x: rect.x + rect.width, y: bottom }
  ];
}

type EdgeCornerRays = {
  leftOrigin: PositionType;
  rightOrigin: PositionType;
  outward: { x: number; y: number };
};

function getEdgeCornerOrigins(
  rect: PositionAndDimensionsType,
  edge: Edge
): EdgeCornerRays {
  const tl = { x: rect.x, y: rect.y };
  const tr = { x: rect.x + rect.width, y: rect.y };
  const br = { x: rect.x + rect.width, y: rect.y + rect.height };
  const bl = { x: rect.x, y: rect.y + rect.height };

  if (edge === 'top') {
    return { leftOrigin: tl, rightOrigin: tr, outward: { x: 0, y: -1 } };
  }
  if (edge === 'right') {
    return { leftOrigin: tr, rightOrigin: br, outward: { x: 1, y: 0 } };
  }
  if (edge === 'bottom') {
    return { leftOrigin: br, rightOrigin: bl, outward: { x: 0, y: 1 } };
  }
  return { leftOrigin: bl, rightOrigin: tl, outward: { x: -1, y: 0 } };
}

type CornerHit = {
  point: PositionType;
  segmentIndex: number;
};

function castCornerRay(
  origin: PositionType,
  direction: { x: number; y: number },
  footprint: PositionType[]
): CornerHit | null {
  let bestT = Infinity;
  let bestIndex = -1;
  for (let i = 0; i < footprint.length; i += 1) {
    const p1 = footprint[i];
    const p2 = footprint[(i + 1) % footprint.length];
    const t = rayHitsSegment(origin, direction, p1, p2);
    if (t !== null && t >= 0 && t < bestT) {
      bestT = t;
      bestIndex = i;
    }
  }
  if (!Number.isFinite(bestT) || bestIndex < 0) return null;
  return {
    point: {
      x: origin.x + direction.x * bestT,
      y: origin.y + direction.y * bestT
    },
    segmentIndex: bestIndex
  };
}

function collectWalkVertices(
  footprint: PositionType[],
  fromSegmentIdx: number,
  toSegmentIdx: number,
  step: 1 | -1
): PositionType[] {
  const result: PositionType[] = [];
  const n = footprint.length;
  let current = fromSegmentIdx;
  const maxSteps = n + 1;
  for (let i = 0; i < maxSteps; i += 1) {
    if (current === toSegmentIdx) return result;
    const vertexIndex = step === 1 ? (current + 1) % n : current;
    result.push(footprint[vertexIndex]);
    current = (current + step + n) % n;
  }
  return result;
}

function dedupConsecutive(verts: PositionType[]): PositionType[] {
  const eps = 1e-6;
  const out: PositionType[] = [];
  for (const v of verts) {
    const prev = out[out.length - 1];
    if (!prev || Math.abs(prev.x - v.x) > eps || Math.abs(prev.y - v.y) > eps) {
      out.push(v);
    }
  }
  if (out.length >= 2) {
    const first = out[0];
    const last = out[out.length - 1];
    if (Math.abs(first.x - last.x) < eps && Math.abs(first.y - last.y) < eps) {
      out.pop();
    }
  }
  return out;
}

export const FOOTPRINT_SNAP_THRESHOLD = 3;
const SWEEP_OVERLAP_INSET = 1e-3;
const MAX_EDGE_EXTENSION_AREA_RATIO = 1;

export type EdgeIntent = Record<Edge, boolean>;

export function getDropEdgeIntent(
  dropRect: PositionAndDimensionsType,
  footprint: PositionType[]
): EdgeIntent {
  const intent: EdgeIntent = {
    top: false,
    right: false,
    bottom: false,
    left: false
  };
  if (footprint.length < 3) return intent;

  const edges: Edge[] = ['top', 'right', 'bottom', 'left'];
  for (const edge of edges) {
    const { origins, direction } = getEdgeRayOrigins(dropRect, edge);
    let intentForEdge = false;
    let minDistance = Infinity;
    for (const origin of origins) {
      if (!isPointInPolygon(origin, footprint)) {
        intentForEdge = true;
        break;
      }
      let firstHit = Infinity;
      for (let i = 0; i < footprint.length; i += 1) {
        const p1 = footprint[i];
        const p2 = footprint[(i + 1) % footprint.length];
        const t = rayHitsSegment(origin, direction, p1, p2);
        if (t !== null && t > 0 && t < firstHit) firstHit = t;
      }
      if (firstHit < minDistance) minDistance = firstHit;
    }
    if (
      !intentForEdge &&
      Number.isFinite(minDistance) &&
      minDistance <= FOOTPRINT_SNAP_THRESHOLD
    ) {
      intentForEdge = true;
    }
    intent[edge] = intentForEdge;
  }
  return intent;
}

export function distPointToSeg(
  p: PositionType,
  a: PositionType,
  b: PositionType
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq)
  );
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function minVertexDistanceToFootprint(
  rect: PositionAndDimensionsType,
  footprint: PositionType[]
): number {
  const corners = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height }
  ];
  let min = Infinity;
  for (const c of corners) {
    for (let i = 0; i < footprint.length; i += 1) {
      const d = distPointToSeg(
        c,
        footprint[i],
        footprint[(i + 1) % footprint.length]
      );
      if (d < min) min = d;
    }
  }
  return min;
}

function isFootprintSegmentAngled(
  segStart: PositionType,
  segEnd: PositionType
): boolean {
  const isHorizontal = Math.abs(segStart.y - segEnd.y) < AXIS_ALIGN_EPS;
  const isVertical = Math.abs(segStart.x - segEnd.x) < AXIS_ALIGN_EPS;
  return !isHorizontal && !isVertical;
}

export function hasNearbyAngledFootprintSegment(
  rect: PositionAndDimensionsType,
  footprint: PositionType[],
  threshold: number = FOOTPRINT_SNAP_THRESHOLD
): boolean {
  if (footprint.length < 3) return false;
  const corners = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height }
  ];
  for (let i = 0; i < footprint.length; i += 1) {
    const segStart = footprint[i];
    const segEnd = footprint[(i + 1) % footprint.length];
    if (isFootprintSegmentAngled(segStart, segEnd)) {
      for (const corner of corners) {
        if (distPointToSeg(corner, segStart, segEnd) <= threshold) {
          return true;
        }
      }
    }
  }
  return false;
}

function computeEdgeSnapTarget(
  rect: PositionAndDimensionsType,
  edge: Edge,
  footprint: PositionType[],
  otherSpacePolygons: PositionType[][]
): number | null {
  const hits = edgeRayHits(rect, edge, footprint);
  const minDistance = minRayDistance(hits);
  const maxDistance = maxRayDistance(hits);
  if (
    !Number.isFinite(minDistance) ||
    minDistance <= 0 ||
    maxDistance <= 0 ||
    minDistance > FOOTPRINT_SNAP_THRESHOLD
  ) {
    return null;
  }

  const sweep = getExtensionSweepPolygon(
    rect,
    edge,
    maxDistance,
    SWEEP_OVERLAP_INSET
  );
  if (otherSpacePolygons.some((other) => polygonsOverlap(sweep, other))) {
    return null;
  }

  const { leftOrigin, rightOrigin, outward } = getEdgeCornerOrigins(rect, edge);
  const corners = [
    { hit: castCornerRay(leftOrigin, outward, footprint), origin: leftOrigin },
    { hit: castCornerRay(rightOrigin, outward, footprint), origin: rightOrigin }
  ];

  const candidates: number[] = [];
  corners.forEach(({ hit, origin }) => {
    if (!hit) return;
    const dist = Math.hypot(hit.point.x - origin.x, hit.point.y - origin.y);
    if (dist > FOOTPRINT_SNAP_THRESHOLD) return;

    const segIdx = hit.segmentIndex;
    const segStart = footprint[segIdx];
    const segEnd = footprint[(segIdx + 1) % footprint.length];
    let candidate: number;
    if (edge === 'left' || edge === 'right') {
      const isVerticalSegment =
        Math.abs(segStart.x - segEnd.x) < AXIS_ALIGN_EPS;
      if (isVerticalSegment) {
        candidate = segStart.x;
      } else if (edge === 'left') {
        candidate = Math.min(segStart.x, segEnd.x);
      } else {
        candidate = Math.max(segStart.x, segEnd.x);
      }
    } else {
      const isHorizontalSegment =
        Math.abs(segStart.y - segEnd.y) < AXIS_ALIGN_EPS;
      if (isHorizontalSegment) {
        candidate = segStart.y;
      } else if (edge === 'top') {
        candidate = Math.min(segStart.y, segEnd.y);
      } else {
        candidate = Math.max(segStart.y, segEnd.y);
      }
    }
    candidates.push(candidate);
  });

  if (candidates.length === 0) return null;

  let snapValue: number;
  if (edge === 'left' || edge === 'top') {
    snapValue = Math.min(...candidates);
  } else {
    snapValue = Math.max(...candidates);
  }

  let original: number;
  switch (edge) {
    case 'left':
      original = rect.x;
      break;
    case 'right':
      original = rect.x + rect.width;
      break;
    case 'top':
      original = rect.y;
      break;
    default:
      original = rect.y + rect.height;
      break;
  }
  const snapDistance = Math.abs(original - snapValue);
  if (snapDistance < AXIS_ALIGN_EPS) return null;

  const edgeLength =
    edge === 'left' || edge === 'right' ? rect.height : rect.width;
  const addedArea = snapDistance * edgeLength;
  const rectArea = rect.width * rect.height;
  if (addedArea > rectArea * MAX_EDGE_EXTENSION_AREA_RATIO) {
    return null;
  }

  return snapValue;
}

function findSegmentIntersection(
  a1: PositionType,
  a2: PositionType,
  b1: PositionType,
  b2: PositionType
): { point: PositionType; tA: number; tB: number } | null {
  const ax = a2.x - a1.x;
  const ay = a2.y - a1.y;
  const bx = b2.x - b1.x;
  const by = b2.y - b1.y;
  const denom = ax * by - ay * bx;
  if (Math.abs(denom) < 1e-9) return null;
  const tA = ((b1.x - a1.x) * by - (b1.y - a1.y) * bx) / denom;
  const tB = ((b1.x - a1.x) * ay - (b1.y - a1.y) * ax) / denom;
  const eps = 1e-9;
  if (tA < -eps || tA > 1 + eps) return null;
  if (tB < -eps || tB > 1 + eps) return null;
  return {
    point: { x: a1.x + ax * tA, y: a1.y + ay * tA },
    tA,
    tB
  };
}

type FootprintCrossing = {
  point: PositionType;
  segmentIndex: number;
  t: number;
};

function findFootprintCrossings(
  start: PositionType,
  end: PositionType,
  footprint: PositionType[]
): FootprintCrossing[] {
  const crossings: FootprintCrossing[] = [];
  for (let i = 0; i < footprint.length; i += 1) {
    const fp1 = footprint[i];
    const fp2 = footprint[(i + 1) % footprint.length];
    const inter = findSegmentIntersection(start, end, fp1, fp2);
    if (inter) {
      crossings.push({
        point: inter.point,
        segmentIndex: i,
        t: inter.tA
      });
    }
  }
  crossings.sort((a, b) => a.t - b.t);
  return crossings;
}

function walkFootprintInsideRect(
  footprint: PositionType[],
  fromSegmentIdx: number,
  toSegmentIdx: number,
  snappedRectPolygon: PositionType[]
): PositionType[] {
  if (fromSegmentIdx === toSegmentIdx) return [];
  const cw = collectWalkVertices(footprint, fromSegmentIdx, toSegmentIdx, 1);
  const ccw = collectWalkVertices(footprint, fromSegmentIdx, toSegmentIdx, -1);
  const cwOk = cw.every((v) => isPointInsideOrOnPolygon(v, snappedRectPolygon));
  const ccwOk = ccw.every((v) =>
    isPointInsideOrOnPolygon(v, snappedRectPolygon)
  );
  if (cwOk && ccwOk) return cw.length <= ccw.length ? cw : ccw;
  if (cwOk) return cw;
  if (ccwOk) return ccw;
  return [];
}

type ClippedSnappedRectangle = {
  vertices: PositionType[];
  cornerOutputs: Map<number, { exit: PositionType; entry: PositionType }>;
};

function clipSnappedRectangleToFootprint(
  snappedCorners: PositionType[],
  footprint: PositionType[]
): ClippedSnappedRectangle | null {
  const cornerOutputs = new Map<
    number,
    { exit: PositionType; entry: PositionType }
  >();
  const result: PositionType[] = [];

  [0, 1, 2, 3].forEach((i) => {
    const corner = snappedCorners[i];
    const prev = snappedCorners[(i + 3) % 4];
    const next = snappedCorners[(i + 1) % 4];

    if (isPointInsideOrOnPolygon(corner, footprint)) {
      result.push(corner);
      return;
    }

    const incomingCrossings = findFootprintCrossings(prev, corner, footprint);
    const outgoingCrossings = findFootprintCrossings(corner, next, footprint);

    const exit =
      incomingCrossings.length > 0
        ? incomingCrossings[incomingCrossings.length - 1]
        : null;
    const entry = outgoingCrossings.length > 0 ? outgoingCrossings[0] : null;

    if (!exit || !entry) {
      result.push(corner);
      return;
    }

    result.push(exit.point);
    const walk = walkFootprintInsideRect(
      footprint,
      exit.segmentIndex,
      entry.segmentIndex,
      snappedCorners
    );
    result.push(...walk);
    result.push(entry.point);

    cornerOutputs.set(i, { exit: exit.point, entry: entry.point });
  });

  const deduped = dedupConsecutive(result);
  if (deduped.length < 3) return null;
  return { vertices: deduped, cornerOutputs };
}

function lineLineIntersection(
  a1: PositionType,
  a2: PositionType,
  b1: PositionType,
  b2: PositionType
): PositionType | null {
  const ax = a2.x - a1.x;
  const ay = a2.y - a1.y;
  const bx = b2.x - b1.x;
  const by = b2.y - b1.y;
  const denom = ax * by - ay * bx;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((b1.x - a1.x) * by - (b1.y - a1.y) * bx) / denom;
  return { x: a1.x + ax * t, y: a1.y + ay * t };
}

export function isPolygonSimple(vertices: RelativeVertex[]): boolean {
  const n = vertices.length;
  if (n < 4) return true;

  for (let i = 0; i < n; i += 1) {
    const a1 = vertices[i];
    const a2 = vertices[(i + 1) % n];
    for (let j = i + 1; j < n; j += 1) {
      if (j !== i && (j + 1) % n !== i) {
        const b1 = vertices[j];
        const b2 = vertices[(j + 1) % n];
        if (segmentsIntersect(a1, a2, b1, b2)) return false;
      }
    }
  }

  return true;
}

export type ExtendEdgeResult = {
  vertices: PositionType[];
  extendedEdges: AutoExtendedEdge[];
};

export function extendEdgeToFootprint(
  rect: PositionAndDimensionsType,
  footprint: PositionType[],
  otherSpacePolygons: PositionType[][],
  eligibleEdges?: EdgeIntent
): ExtendEdgeResult | null {
  if (footprint.length < 3) return null;
  if (minVertexDistanceToFootprint(rect, footprint) > FOOTPRINT_SNAP_THRESHOLD)
    return null;

  const edges: Edge[] = ['top', 'right', 'bottom', 'left'];
  const snapTargets: Partial<Record<Edge, number>> = {};
  for (const edge of edges) {
    if (!eligibleEdges || eligibleEdges[edge]) {
      const snap = computeEdgeSnapTarget(
        rect,
        edge,
        footprint,
        otherSpacePolygons
      );
      if (snap !== null) snapTargets[edge] = snap;
    }
  }
  if (Object.keys(snapTargets).length === 0) return null;

  const snappedTop = snapTargets.top ?? rect.y;
  const snappedRight = snapTargets.right ?? rect.x + rect.width;
  const snappedBottom = snapTargets.bottom ?? rect.y + rect.height;
  const snappedLeft = snapTargets.left ?? rect.x;

  const snappedCorners: PositionType[] = [
    { x: snappedLeft, y: snappedTop },
    { x: snappedRight, y: snappedTop },
    { x: snappedRight, y: snappedBottom },
    { x: snappedLeft, y: snappedBottom }
  ];

  const clipped = clipSnappedRectangleToFootprint(snappedCorners, footprint);
  if (!clipped) return null;

  const edgeCorners: Record<
    Edge,
    { prevA: PositionType; prevB: PositionType }
  > = {
    top: {
      prevA: { x: rect.x, y: rect.y },
      prevB: { x: rect.x + rect.width, y: rect.y }
    },
    right: {
      prevA: { x: rect.x + rect.width, y: rect.y },
      prevB: { x: rect.x + rect.width, y: rect.y + rect.height }
    },
    bottom: {
      prevA: { x: rect.x + rect.width, y: rect.y + rect.height },
      prevB: { x: rect.x, y: rect.y + rect.height }
    },
    left: {
      prevA: { x: rect.x, y: rect.y + rect.height },
      prevB: { x: rect.x, y: rect.y }
    }
  };

  const edgeStartCorner: Record<Edge, number> = {
    top: 0,
    right: 1,
    bottom: 2,
    left: 3
  };
  const edgeEndCorner: Record<Edge, number> = {
    top: 1,
    right: 2,
    bottom: 3,
    left: 0
  };

  const extendedEdges: AutoExtendedEdge[] = edges
    .filter((edge) => snapTargets[edge] !== undefined)
    .map((edge) => {
      const startIdx = edgeStartCorner[edge];
      const endIdx = edgeEndCorner[edge];
      const startOutput = clipped.cornerOutputs.get(startIdx);
      const endOutput = clipped.cornerOutputs.get(endIdx);
      const currA = startOutput ? startOutput.entry : snappedCorners[startIdx];
      const currB = endOutput ? endOutput.exit : snappedCorners[endIdx];
      return {
        prevA: edgeCorners[edge].prevA,
        prevB: edgeCorners[edge].prevB,
        currA: { x: currA.x, y: currA.y },
        currB: { x: currB.x, y: currB.y }
      };
    });

  return { vertices: clipped.vertices, extendedEdges };
}

type PolygonEdgeExtension = {
  leftHit: PositionType | null;
  rightHit: PositionType | null;
};

function computePolygonOutwardNormal(
  vertices: PositionType[],
  edgeIndex: number
): { x: number; y: number } | null {
  const n = vertices.length;
  const p1 = vertices[edgeIndex];
  const p2 = vertices[(edgeIndex + 1) % n];
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const length = Math.hypot(dx, dy);
  if (length < NORMAL_EPS) return null;
  const candidate = { x: dy / length, y: -dx / length };
  const midX = (p1.x + p2.x) / 2;
  const midY = (p1.y + p2.y) / 2;
  const probe = {
    x: midX + candidate.x * AXIS_ALIGN_EPS,
    y: midY + candidate.y * AXIS_ALIGN_EPS
  };
  return isPointInPolygon(probe, vertices)
    ? { x: -candidate.x, y: -candidate.y }
    : candidate;
}

export function projectPointOnSegment(
  p: PositionType,
  a: PositionType,
  b: PositionType
): PositionType {
  const ax = b.x - a.x;
  const ay = b.y - a.y;
  const lenSq = ax * ax + ay * ay;
  if (lenSq < 1e-12) return { x: a.x, y: a.y };
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * ax + (p.y - a.y) * ay) / lenSq)
  );
  return { x: a.x + ax * t, y: a.y + ay * t };
}

function closestPointOnPolygonBoundary(
  point: PositionType,
  polygon: PositionType[]
): PositionType {
  let best: PositionType = polygon[0];
  let bestDistSq = Infinity;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const proj = projectPointOnSegment(point, a, b);
    const dx = proj.x - point.x;
    const dy = proj.y - point.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = proj;
    }
  }
  return best;
}

function computePolygonEdgeExtension(
  vertices: PositionType[],
  edgeIndex: number,
  outward: { x: number; y: number },
  footprint: PositionType[],
  otherSpacePolygons: PositionType[][]
): PolygonEdgeExtension | null {
  const n = vertices.length;
  const p1 = vertices[edgeIndex];
  const p2 = vertices[(edgeIndex + 1) % n];
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const edgeLength = Math.hypot(dx, dy);
  if (edgeLength < NORMAL_EPS) return null;

  const sampleCount = Math.max(3, Math.ceil(edgeLength) + 1);
  const hits: number[] = [];
  for (let s = 0; s < sampleCount; s += 1) {
    const t = s / (sampleCount - 1);
    const origin = { x: p1.x + dx * t, y: p1.y + dy * t };
    let firstHit = Infinity;
    for (let j = 0; j < footprint.length; j += 1) {
      const f1 = footprint[j];
      const f2 = footprint[(j + 1) % footprint.length];
      const hit = rayHitsSegment(origin, outward, f1, f2);
      if (hit !== null && hit > 0 && hit < firstHit) firstHit = hit;
    }
    if (Number.isFinite(firstHit)) hits.push(firstHit);
  }

  const minDistance = minRayDistance(hits);
  const maxDistance = maxRayDistance(hits);
  if (
    !Number.isFinite(minDistance) ||
    minDistance <= 0 ||
    maxDistance <= 0 ||
    minDistance > FOOTPRINT_SNAP_THRESHOLD
  ) {
    return null;
  }

  const insetT = SWEEP_OVERLAP_INSET / edgeLength;
  const sweepP1Inset = {
    x: p1.x + dx * insetT,
    y: p1.y + dy * insetT
  };
  const sweepP2Inset = {
    x: p2.x - dx * insetT,
    y: p2.y - dy * insetT
  };
  const sweep = [
    sweepP1Inset,
    sweepP2Inset,
    {
      x: sweepP2Inset.x + outward.x * maxDistance,
      y: sweepP2Inset.y + outward.y * maxDistance
    },
    {
      x: sweepP1Inset.x + outward.x * maxDistance,
      y: sweepP1Inset.y + outward.y * maxDistance
    }
  ];
  if (otherSpacePolygons.some((other) => polygonsOverlap(sweep, other))) {
    return null;
  }

  const leftHitRaw = castCornerRay(p1, outward, footprint);
  const rightHitRaw = castCornerRay(p2, outward, footprint);
  if (!leftHitRaw && !rightHitRaw) return null;

  const leftDist = leftHitRaw
    ? Math.hypot(leftHitRaw.point.x - p1.x, leftHitRaw.point.y - p1.y)
    : Infinity;
  const rightDist = rightHitRaw
    ? Math.hypot(rightHitRaw.point.x - p2.x, rightHitRaw.point.y - p2.y)
    : Infinity;

  const leftValid = leftHitRaw !== null && leftDist <= FOOTPRINT_SNAP_THRESHOLD;
  const rightValid =
    rightHitRaw !== null && rightDist <= FOOTPRINT_SNAP_THRESHOLD;
  if (!leftValid && !rightValid) return null;

  const effectiveLeftDist = leftValid ? leftDist : 0;
  const effectiveRightDist = rightValid ? rightDist : 0;
  const addedArea = ((effectiveLeftDist + effectiveRightDist) / 2) * edgeLength;
  const polygonAreaVal = polygonArea(vertices);
  if (
    polygonAreaVal > 0 &&
    addedArea > polygonAreaVal * MAX_EDGE_EXTENSION_AREA_RATIO
  ) {
    return null;
  }

  return {
    leftHit: leftValid ? leftHitRaw!.point : null,
    rightHit: rightValid ? rightHitRaw!.point : null
  };
}

export type ExtendPolygonResult = {
  vertices: PositionType[];
  extendedEdges: AutoExtendedEdge[];
};

export function extendPolygonShapeToFootprint(
  absVertices: PositionType[],
  footprint: PositionType[],
  otherSpacePolygons: PositionType[][]
): ExtendPolygonResult | null {
  if (footprint.length < 3 || absVertices.length < 3) return null;

  const n = absVertices.length;
  const extensions: (PolygonEdgeExtension | null)[] = [];
  let anyExtended = false;
  for (let i = 0; i < n; i += 1) {
    const outward = computePolygonOutwardNormal(absVertices, i);
    if (!outward) {
      extensions.push(null);
    } else {
      const ext = computePolygonEdgeExtension(
        absVertices,
        i,
        outward,
        footprint,
        otherSpacePolygons
      );
      extensions.push(ext);
      if (ext) anyExtended = true;
    }
  }

  if (!anyExtended) return null;

  const newVertices: PositionType[] = [];
  for (let i = 0; i < n; i += 1) {
    const prevIdx = (i - 1 + n) % n;
    const prevExt = extensions[prevIdx];
    const nextExt = extensions[i];
    const incomingHit = prevExt?.rightHit ?? null;
    const outgoingHit = nextExt?.leftHit ?? null;

    if (incomingHit && outgoingHit) {
      const canIntersect =
        !!prevExt?.leftHit &&
        !!prevExt?.rightHit &&
        !!nextExt?.leftHit &&
        !!nextExt?.rightHit;
      if (canIntersect) {
        const prevP1 = absVertices[prevIdx];
        const prevP2 = absVertices[i];
        const nextP1 = absVertices[i];
        const nextP2 = absVertices[(i + 1) % n];
        const prevA2 = {
          x: prevExt!.rightHit!.x + (prevP2.x - prevP1.x),
          y: prevExt!.rightHit!.y + (prevP2.y - prevP1.y)
        };
        const nextA2 = {
          x: nextExt!.leftHit!.x + (nextP2.x - nextP1.x),
          y: nextExt!.leftHit!.y + (nextP2.y - nextP1.y)
        };
        const intersect = lineLineIntersection(
          prevExt!.rightHit!,
          prevA2,
          nextExt!.leftHit!,
          nextA2
        );
        if (!intersect) {
          newVertices.push(incomingHit);
        } else if (isPointInsideOrOnPolygon(intersect, footprint)) {
          newVertices.push(intersect);
        } else {
          newVertices.push(closestPointOnPolygonBoundary(intersect, footprint));
        }
      } else {
        newVertices.push(incomingHit);
      }
    } else if (incomingHit) {
      newVertices.push(incomingHit);
    } else if (outgoingHit) {
      newVertices.push(outgoingHit);
    } else {
      newVertices.push(absVertices[i]);
    }
  }

  if (newVertices.length < 3) return null;
  if (!isPolygonSimple(newVertices)) return null;

  const extendedEdges: AutoExtendedEdge[] = [];
  for (let i = 0; i < n; i += 1) {
    if (extensions[i]) {
      const nextIdx = (i + 1) % n;
      extendedEdges.push({
        prevA: { x: absVertices[i].x, y: absVertices[i].y },
        prevB: { x: absVertices[nextIdx].x, y: absVertices[nextIdx].y },
        currA: { x: newVertices[i].x, y: newVertices[i].y },
        currB: { x: newVertices[nextIdx].x, y: newVertices[nextIdx].y }
      });
    }
  }

  return { vertices: dedupConsecutive(newVertices), extendedEdges };
}

const REVERT_VERTEX_MATCH_EPS = 1e-3;

function findVertexIndex(
  vertices: PositionType[],
  target: PositionType
): number {
  for (let i = 0; i < vertices.length; i += 1) {
    if (
      Math.abs(vertices[i].x - target.x) < REVERT_VERTEX_MATCH_EPS &&
      Math.abs(vertices[i].y - target.y) < REVERT_VERTEX_MATCH_EPS
    ) {
      return i;
    }
  }
  return -1;
}

function cyclicSliceLength(
  idxStart: number,
  idxEnd: number,
  n: number
): number {
  return ((((idxEnd - idxStart) % n) + n) % n) + 1;
}

type RevertAction =
  | { kind: 'keep' }
  | { kind: 'replace'; point: PositionType }
  | { kind: 'remove' };

export function revertExtendedEdges(
  absVertices: PositionType[],
  records: AutoExtendedEdge[]
): PositionType[] {
  if (records.length === 0 || absVertices.length < 3) return absVertices;
  const n = absVertices.length;
  const actions: RevertAction[] = absVertices.map(() => ({ kind: 'keep' }));

  for (const record of records) {
    const idxA = findVertexIndex(absVertices, record.currA);
    const idxB = findVertexIndex(absVertices, record.currB);
    if (idxA !== -1 && idxB !== -1 && idxA !== idxB) {
      const forwardLen = cyclicSliceLength(idxA, idxB, n);
      const backwardLen = cyclicSliceLength(idxB, idxA, n);

      let sliceStart: number;
      let sliceEnd: number;
      let prevStart: PositionType;
      let prevEnd: PositionType;
      if (forwardLen <= backwardLen) {
        sliceStart = idxA;
        sliceEnd = idxB;
        prevStart = record.prevA;
        prevEnd = record.prevB;
      } else {
        sliceStart = idxB;
        sliceEnd = idxA;
        prevStart = record.prevB;
        prevEnd = record.prevA;
      }

      actions[sliceStart] = {
        kind: 'replace',
        point: { x: prevStart.x, y: prevStart.y }
      };
      actions[sliceEnd] = {
        kind: 'replace',
        point: { x: prevEnd.x, y: prevEnd.y }
      };

      let i = (sliceStart + 1) % n;
      while (i !== sliceEnd) {
        const current = actions[i];
        if (current.kind === 'keep') {
          actions[i] = { kind: 'remove' };
        }
        i = (i + 1) % n;
      }
    }
  }

  const result: PositionType[] = [];
  for (let i = 0; i < n; i += 1) {
    const action = actions[i];
    if (action.kind === 'replace') {
      result.push(action.point);
    } else if (action.kind === 'keep') {
      result.push(absVertices[i]);
    }
  }

  if (result.length < 3) return absVertices;

  return dedupConsecutive(result);
}

export type SpaceShapeUpdate = {
  position: { x: number; y: number };
  vertices?: RelativeVertex[];
  width: number | undefined;
  length: number | undefined;
  writeShape: boolean;
  extendedEdges: AutoExtendedEdge[];
};

export type BuildSpaceShapeWithFootprintExtensionParams = {
  fitted: PositionAndDimensionsType;
  footprintPolygons: FootprintPolygonsType | null;
  otherSpaces: GridSpaceDataType[];
  excludeSpaceId?: string;
  forceWrite: boolean;
  dropRect?: PositionAndDimensionsType;
};

export type BuildSpaceShapeWithFootprintExtensionFromPolygonsParams = {
  fitted: PositionAndDimensionsType;
  footprintPolygons: FootprintPolygonsType | null;
  otherPolygons: PositionType[][];
  forceWrite: boolean;
  dropRect?: PositionAndDimensionsType;
};

function probeEdgeInterior(
  polygon: PositionType[],
  edgeIndex: number,
  epsilon: number = 0.01
): PositionType | null {
  const p1 = polygon[edgeIndex];
  const p2 = polygon[(edgeIndex + 1) % polygon.length];
  const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return null;
  const nx = -dy / len;
  const ny = dx / len;
  const c1 = { x: mid.x + nx * epsilon, y: mid.y + ny * epsilon };
  if (isPointInPolygon(c1, polygon)) return c1;
  const c2 = { x: mid.x - nx * epsilon, y: mid.y - ny * epsilon };
  if (isPointInPolygon(c2, polygon)) return c2;
  return null;
}

export function polygonsOverlapStrict(
  a: PositionType[],
  b: PositionType[]
): boolean {
  if (a.length < 3 || b.length < 3) return false;

  for (let i = 0; i < a.length; i += 1) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j += 1) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }

  for (let i = 0; i < a.length; i += 1) {
    const probe = probeEdgeInterior(a, i);
    if (probe && isPointInPolygon(probe, b)) return true;
  }
  for (let j = 0; j < b.length; j += 1) {
    const probe = probeEdgeInterior(b, j);
    if (probe && isPointInPolygon(probe, a)) return true;
  }

  return false;
}

export function isPolygonInsideFootprint(
  absVertices: PositionType[],
  footprint: PositionType[]
): boolean {
  if (footprint.length < 3) return true;
  if (absVertices.length < 3) return true;

  for (const v of absVertices) {
    if (!isPointInsideOrOnPolygon(v, footprint)) return false;
  }

  for (let i = 0; i < absVertices.length; i += 1) {
    const a1 = absVertices[i];
    const a2 = absVertices[(i + 1) % absVertices.length];
    for (let j = 0; j < footprint.length; j += 1) {
      const b1 = footprint[j];
      const b2 = footprint[(j + 1) % footprint.length];
      if (segmentsIntersect(a1, a2, b1, b2)) return false;
    }
  }

  return true;
}

export function isPolygonInsideAnyFootprint(
  absVertices: PositionType[],
  footprints: FootprintPolygonsType | null | undefined
): boolean {
  const valid = validFootprints(footprints);
  if (valid.length === 0) return true;
  return valid.some((footprint) =>
    isPolygonInsideFootprint(absVertices, footprint)
  );
}

export function polygonOverlapsAny(
  candidate: PositionType[],
  others: PositionType[][]
): boolean {
  return others.some((other) => polygonsOverlapStrict(candidate, other));
}

function gridSpaceToAbsolutePolygon(s: GridSpaceDataType): PositionType[] {
  if (s.vertices && s.vertices.length >= 3) {
    return s.vertices.map((v) => ({
      x: (s.position.x + v.x) / STRUCTURE_GRID_SIZE,
      y: (s.position.y + v.y) / STRUCTURE_GRID_SIZE
    }));
  }
  return rectToAbsolutePolygon({
    x: s.position.x / STRUCTURE_GRID_SIZE,
    y: s.position.y / STRUCTURE_GRID_SIZE,
    width: s.position.width / STRUCTURE_GRID_SIZE,
    height: s.position.height / STRUCTURE_GRID_SIZE
  });
}

export function extendRectAsPolygonToFootprint(
  rect: PositionAndDimensionsType,
  footprint: PositionType[],
  otherSpacePolygons: PositionType[][]
): ExtendEdgeResult | null {
  const absVertices = rectToAbsolutePolygon(rect);
  const extended = extendPolygonShapeToFootprint(
    absVertices,
    footprint,
    otherSpacePolygons
  );
  if (!extended || extended.vertices.length < 3) {
    return null;
  }
  if (!isPolygonInsideFootprint(extended.vertices, footprint)) {
    return null;
  }
  if (polygonOverlapsAny(extended.vertices, otherSpacePolygons)) {
    return null;
  }
  return {
    vertices: extended.vertices,
    extendedEdges: extended.extendedEdges
  };
}

function computeFootprintExtension(
  fitted: PositionAndDimensionsType,
  footprint: PositionType[],
  otherPolygons: PositionType[][],
  dropRect: PositionAndDimensionsType | undefined
): ExtendEdgeResult | null {
  if (hasNearbyAngledFootprintSegment(fitted, footprint)) {
    const polygonResult = extendRectAsPolygonToFootprint(
      fitted,
      footprint,
      otherPolygons
    );
    if (polygonResult) {
      return polygonResult;
    }
  }

  const eligibleEdges = dropRect
    ? getDropEdgeIntent(dropRect, footprint)
    : undefined;
  return extendEdgeToFootprint(fitted, footprint, otherPolygons, eligibleEdges);
}

export function buildSpaceShapeWithFootprintExtensionFromPolygons({
  fitted,
  footprintPolygons,
  otherPolygons,
  forceWrite,
  dropRect
}: BuildSpaceShapeWithFootprintExtensionFromPolygonsParams): SpaceShapeUpdate {
  const baseline: SpaceShapeUpdate = {
    position: { x: fitted.x, y: fitted.y },
    vertices: rectToVertices(fitted.width, fitted.height),
    width: fitted.width,
    length: fitted.height,
    writeShape: forceWrite,
    extendedEdges: []
  };

  const footprint = findContainingPolygon(
    fitted,
    validFootprints(footprintPolygons)
  );
  if (!footprint) {
    return baseline;
  }

  const extended = computeFootprintExtension(
    fitted,
    footprint,
    otherPolygons,
    dropRect
  );
  if (!extended || extended.vertices.length < 3) {
    return baseline;
  }

  const relativeToFitted = extended.vertices.map((p) => ({
    x: p.x - fitted.x,
    y: p.y - fitted.y
  }));
  const extendedBbox = boundingBoxOfVertices(relativeToFitted);
  const normalizedVertices = relativeToFitted.map((v) => ({
    x: v.x - extendedBbox.minX,
    y: v.y - extendedBbox.minY
  }));
  const isRect = isRectangleBboxVertices(
    normalizedVertices,
    extendedBbox.width,
    extendedBbox.height
  );

  return {
    position: {
      x: fitted.x + extendedBbox.minX,
      y: fitted.y + extendedBbox.minY
    },
    vertices: normalizedVertices,
    width: isRect ? fitted.width : undefined,
    length: isRect ? fitted.height : undefined,
    writeShape: true,
    extendedEdges: extended.extendedEdges
  };
}

export function buildSpaceShapeWithFootprintExtension({
  fitted,
  footprintPolygons,
  otherSpaces,
  excludeSpaceId,
  forceWrite,
  dropRect
}: BuildSpaceShapeWithFootprintExtensionParams): SpaceShapeUpdate {
  return buildSpaceShapeWithFootprintExtensionFromPolygons({
    fitted,
    footprintPolygons,
    otherPolygons: otherSpaces
      .filter((s) => s.spaceId !== excludeSpaceId)
      .map(gridSpaceToAbsolutePolygon),
    forceWrite,
    dropRect
  });
}

export type EdgeCardinal = 'top' | 'right' | 'bottom' | 'left';

export type SpacePolygonEdge = {
  edgeIndex: number;
  p1: PositionType;
  p2: PositionType;
  outward: PositionType;
  isExterior: boolean;
  cardinal: EdgeCardinal | null;
};

export function getExteriorPolygonEdges(
  absoluteVertices: PositionType[],
  footprints: FootprintPolygonsType | null
): SpacePolygonEdge[] {
  if (absoluteVertices.length < 3) return [];

  const eps = 0.01;
  const result: SpacePolygonEdge[] = [];
  const validFootprintList = validFootprints(footprints);
  const hasFootprint = validFootprintList.length > 0;

  for (let i = 0; i < absoluteVertices.length; i += 1) {
    const p1 = absoluteVertices[i];
    const p2 = absoluteVertices[(i + 1) % absoluteVertices.length];

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const length = Math.hypot(dx, dy);
    if (length >= eps) {
      const midX = (p1.x + p2.x) / 2;
      const midY = (p1.y + p2.y) / 2;

      const candidateNormal = { x: dy / length + 0, y: -dx / length + 0 };
      const probeInside = {
        x: midX + candidateNormal.x * eps,
        y: midY + candidateNormal.y * eps
      };
      const outward = isPointInPolygon(probeInside, absoluteVertices)
        ? { x: -candidateNormal.x + 0, y: -candidateNormal.y + 0 }
        : candidateNormal;

      const probe = {
        x: midX + outward.x * eps,
        y: midY + outward.y * eps
      };
      const isExterior =
        !hasFootprint || !isPointInAnyPolygon(probe, validFootprintList);

      let cardinal: EdgeCardinal | null = null;
      if (Math.abs(dy) < eps) {
        if (outward.y < 0) cardinal = 'top';
        else if (outward.y > 0) cardinal = 'bottom';
      } else if (Math.abs(dx) < eps) {
        if (outward.x > 0) cardinal = 'right';
        else if (outward.x < 0) cardinal = 'left';
      } else {
        cardinal = edgeForOutwardNormal(outward);
      }

      result.push({ edgeIndex: i, p1, p2, outward, isExterior, cardinal });
    }
  }

  return result;
}

export type AngledEdgeDragAxis = 'horizontal' | 'vertical';

export function isEdgeAxisAligned(
  vertices: RelativeVertex[],
  edgeIndex: number
): boolean {
  if (vertices.length < 2) return false;
  const p1 = vertices[edgeIndex];
  const p2 = vertices[(edgeIndex + 1) % vertices.length];
  return (
    Math.abs(p1.x - p2.x) < AXIS_ALIGN_EPS ||
    Math.abs(p1.y - p2.y) < AXIS_ALIGN_EPS
  );
}

export function getEdgeOutwardNormal(
  vertices: RelativeVertex[],
  edgeIndex: number
): { x: number; y: number } {
  if (vertices.length < 3) return { x: 0, y: 0 };

  const p1 = vertices[edgeIndex];
  const p2 = vertices[(edgeIndex + 1) % vertices.length];
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const length = Math.hypot(dx, dy);
  if (length < NORMAL_EPS) return { x: 0, y: 0 };

  const candidate = { x: dy / length, y: -dx / length };
  const midX = (p1.x + p2.x) / 2;
  const midY = (p1.y + p2.y) / 2;
  const probe = {
    x: midX + candidate.x * AXIS_ALIGN_EPS,
    y: midY + candidate.y * AXIS_ALIGN_EPS
  };
  if (isPointInPolygon(probe, vertices)) {
    return { x: -candidate.x, y: -candidate.y };
  }
  return candidate;
}

export function angledEdgeDragAxis(
  vertices: RelativeVertex[],
  edgeIndex: number
): AngledEdgeDragAxis {
  const normal = getEdgeOutwardNormal(vertices, edgeIndex);
  return Math.abs(normal.x) > Math.abs(normal.y) ? 'horizontal' : 'vertical';
}

export function translatePolygonEdge(
  vertices: RelativeVertex[],
  edgeIndex: number,
  dx: number,
  dy: number
): RelativeVertex[] {
  if (vertices.length < 2) return vertices;
  const next = (edgeIndex + 1) % vertices.length;
  return vertices.map((v, i) => {
    if (i === edgeIndex || i === next) {
      return { x: v.x + dx, y: v.y + dy };
    }
    return v;
  });
}

export function setPolygonVertex(
  vertices: RelativeVertex[],
  vertexIndex: number,
  newPos: RelativeVertex
): RelativeVertex[] {
  return vertices.map((v, i) => (i === vertexIndex ? newPos : v));
}

export function polygonInteriorPoint(
  vertices: PositionType[]
): PositionType | null {
  if (vertices.length < 3) return null;

  const bb = polygonBoundingBox(vertices);
  const bboxCenter = {
    x: (bb.minX + bb.maxX) / 2,
    y: (bb.minY + bb.maxY) / 2
  };
  if (isPointInPolygon(bboxCenter, vertices)) return bboxCenter;

  for (let i = 0; i < vertices.length - 2; i += 1) {
    const candidate = {
      x: (vertices[i].x + vertices[i + 1].x + vertices[i + 2].x) / 3,
      y: (vertices[i].y + vertices[i + 1].y + vertices[i + 2].y) / 3
    };
    if (isPointInPolygon(candidate, vertices)) return candidate;
  }

  const avg = vertices.reduce(
    (acc, v) => ({ x: acc.x + v.x, y: acc.y + v.y }),
    { x: 0, y: 0 }
  );
  avg.x /= vertices.length;
  avg.y /= vertices.length;
  return isPointInPolygon(avg, vertices) ? avg : null;
}

export function buildOtherSpacePolygonsAbsolute(
  others: GridSpaceDataType[],
  excludeSpaceId?: string
): PositionType[][] {
  return others
    .filter((s) => s.spaceId !== excludeSpaceId)
    .map(gridSpaceToAbsolutePolygon);
}

export function filterSpacesOverlappingRect(
  others: GridSpaceDataType[],
  rectFeet: PositionAndDimensionsType
): GridSpaceDataType[] {
  const rectPolygon = rectToAbsolutePolygon(rectFeet);
  return others.filter((s) =>
    polygonsOverlapStrict(rectPolygon, gridSpaceToAbsolutePolygon(s))
  );
}

export type BboxCornerHandle = 'nw' | 'ne' | 'sw' | 'se';

export function getBboxCornerForVertex(
  vertices: RelativeVertex[],
  vertexIndex: number,
  width: number,
  height: number,
  tolerance: number = 0.5
): BboxCornerHandle | null {
  if (vertexIndex < 0 || vertexIndex >= vertices.length) return null;
  const v = vertices[vertexIndex];
  const atLeft = Math.abs(v.x) <= tolerance;
  const atRight = Math.abs(v.x - width) <= tolerance;
  const atTop = Math.abs(v.y) <= tolerance;
  const atBottom = Math.abs(v.y - height) <= tolerance;
  if (atLeft && atTop) return 'nw';
  if (atRight && atTop) return 'ne';
  if (atLeft && atBottom) return 'sw';
  if (atRight && atBottom) return 'se';
  return null;
}

export function getClosestBboxCorner(
  vertex: RelativeVertex,
  width: number,
  height: number
): BboxCornerHandle {
  const left = vertex.x <= width / 2;
  const top = vertex.y <= height / 2;
  if (left && top) return 'nw';
  if (!left && top) return 'ne';
  if (left && !top) return 'sw';
  return 'se';
}

function isSingleFootprintFullForNewSpace(
  footprint: PositionType[],
  otherSpacePolygons: PositionType[][],
  minDim: number
): boolean {
  const bb = polygonBoundingBox(footprint);
  for (let testX = bb.minX; testX + minDim <= bb.maxX; testX += 1) {
    for (let testY = bb.minY; testY + minDim <= bb.maxY; testY += 1) {
      const rect = { x: testX, y: testY, width: minDim, height: minDim };
      const fitsFootprint = isRectInPolygon(rect, footprint);
      const clearOfOthers =
        fitsFootprint &&
        !polygonOverlapsAny(rectToAbsolutePolygon(rect), otherSpacePolygons);
      if (clearOfOthers) {
        return false;
      }
    }
  }

  return true;
}

export function isFootprintFullForNewSpace(
  footprints: FootprintPolygonsType,
  otherSpacePolygons: PositionType[][],
  minDim: number = MIN_SPACE_DIM_FEET
): boolean {
  const valid = validFootprints(footprints);
  if (valid.length === 0) return false;
  return valid.every((footprint) =>
    isSingleFootprintFullForNewSpace(footprint, otherSpacePolygons, minDim)
  );
}
